// components/popup/ProsecutoChoiceRespondBackPopup.tsx
import React, { useState } from "react";
import { BasePopup } from "./BasePopup";
import {
  addReferralRespondBack,
  updateAssetReferral,
} from "services/assetRecoveryService";
import { useTooltip } from "context/TooltipProvider";
import { MultipleFileUploadComponent } from "components/form/MultipleFileUpload";
import {
  AssetReferralActionStatusEnum,
  AssetReferralForwardingStatusEnum,
} from "types";
import { useAuth } from "context/AuthContext";
import { useTranslation } from "react-i18next";

interface ProsecutoChoiceRespondBackPopupProps {
  isOpen: boolean;
  onClose: () => void;
  referralId: string;
  onSuccess?: () => void;
  setActiveTab?: React.Dispatch<React.SetStateAction<string>>;
  onNewDataAdded?: (data: any) => void;
}

export const ProsecutoChoiceRespondBackPopup: React.FC<
  ProsecutoChoiceRespondBackPopupProps
> = ({
  isOpen,
  onClose,
  referralId,
  onSuccess,
  onNewDataAdded,
  setActiveTab,
}) => {
  const [formData, setFormData] = useState({
    feedback_summary: "",
    feedback_date: "",
  });

  const [feedbackFile, setFeedbackFile] = useState<File[]>([]);
  const [loading, setLoading] = useState(false);
  const { showTooltip } = useTooltip();
  const { t } = useTranslation();
  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };
  const { loginData } = useAuth();
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Basic validation
    if (!formData.feedback_summary.trim()) {
      showTooltip(t("please_enter_feedback_summary"), "error");
      return;
    }

    if (!formData.feedback_date) {
      showTooltip(t("please_select_feedback_date"), "error");
      return;
    }

    try {
      setLoading(true);

      // Get user info from sessionStorage

      const userId = loginData?.user_id ?? null;
      const username = loginData?.full_name ?? null;

      // Create FormData for multipart upload
      const submitFormData = new FormData();

      // Append text fields
      submitFormData.append("feedback_summary", formData.feedback_summary);
      submitFormData.append("feedback_date", formData.feedback_date);
      submitFormData.append("given_by_user_id", userId?.toString() || "");
      submitFormData.append("given_by_name", username || "");
      submitFormData.append("referral", referralId);
      submitFormData.append("feedback_type", "respond_back");
      submitFormData.append(
        "respondback_type",
        "Asset Recovery Not Required Request",
      );
      submitFormData.append("role", "coordinator");

      if (feedbackFile.length > 0) {
        feedbackFile.forEach((file) => {
          submitFormData.append("feedback_documents", file);
        });
      }
      // Call API
      await addReferralRespondBack(submitFormData);
      const response = await updateAssetReferral(referralId, {
        is_the_investigation_finished: false,
        forward_status:
          AssetReferralForwardingStatusEnum?.ASSIGNED_TO_PROSECUTOR,
      });

      // Show success message
      showTooltip(t("respond_back_submitted_successfully"), "success");

      // Reset form and close popup
      handleClose();
      if (onNewDataAdded && response) {
        onNewDataAdded(response);
        setActiveTab?.("Investigation");
      }

      // Call success callback if provided
      if (onSuccess) {
        onSuccess();
      }
    } catch (error: any) {
      console.error("Error submitting respond back:", error);
      const errorMessage =
        error.response?.data?.message || t("failed_to_submit_respond_back");
      showTooltip(errorMessage, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    // Reset form when closing
    setFormData({
      feedback_summary: "",
      feedback_date: "",
    });
    setFeedbackFile([]);
    onClose();
  };

  return (
    <BasePopup
      isOpen={isOpen}
      onClose={handleClose}
      title="Add Respond Back"
      description="Add respond back feedback information"
    >
      <form onSubmit={handleSubmit}>
        {/* Feedback Summary */}
        <div className="flex flex-col mt-4">
          <label className="referral-popup-label">Feedback Summary *</label>
          <textarea
            className="referral-form-field h-24"
            onChange={handleInputChange}
            name="feedback_summary"
            placeholder="Enter feedback summary"
            value={formData.feedback_summary}
            required
          />
        </div>

        {/* Feedback Date */}
        <div className="flex flex-col mt-4">
          <label className="referral-popup-label">Feedback Date *</label>
          <input
            type="date"
            className="referral-form-field"
            onChange={handleInputChange}
            name="feedback_date"
            value={formData.feedback_date}
            required
          />
        </div>

        {/* Feedback File Upload */}
        <div className="mt-4">
          <MultipleFileUploadComponent
            onFilesChange={setFeedbackFile}
            uploadedFiles={feedbackFile}
            label="Feedback Document"
          />
        </div>

        {/* Submit Button */}
        <button
          type="submit"
          disabled={loading}
          className={`mt-6 mb-4 w-full referral-form-button ${
            loading ? "opacity-50 cursor-not-allowed" : "hover:bg-gray-800"
          }`}
        >
          {loading ? "Submitting..." : "Submit"}
        </button>
      </form>
    </BasePopup>
  );
};
